/**
 * Image Carousel — image-carousel.js
 *
 * Features:
 *  - Configurable visible slides (desktop + mobile breakpoint)
 *  - Slide / Fade / Slide+Scale transition animations
 *  - Section entrance animation via IntersectionObserver
 *  - Lazy image loading: only fetches images as they approach the viewport
 *  - Autoplay with pause-on-hover and pause-on-focus
 *  - Loop option
 *  - Arrow and dot controls
 *  - Touch/swipe support
 *  - Shopify theme editor lifecycle events
 *  - prefers-reduced-motion respected
 */

(function () {
  'use strict';

  const MOBILE_BP = 750;
  const REDUCED   = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Clamp ── */
  function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

  /* =========================================================
     Lazy image loader
     ========================================================= */
  function createImageObserver() {
    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          const src     = img.dataset.src;
          const srcset  = img.dataset.srcset;
          if (srcset) img.srcset = srcset;
          if (src)    img.src    = src;
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
          img.addEventListener('load',  () => img.classList.add('ic-loaded'), { once: true });
          img.addEventListener('error', () => img.classList.add('ic-loaded'), { once: true });
          obs.unobserve(img);
        });
      },
      { rootMargin: '200px' }   // load 200px before entering view
    );
  }

  const imageObserver = createImageObserver();

  /* =========================================================
     ImageCarousel
     ========================================================= */
  class ImageCarousel {
    constructor(el) {
      this.el           = el;
      this.track        = el.querySelector('.image-carousel__track');
      this.trackWrap    = el.querySelector('.image-carousel__track-wrap');
      this.slides       = Array.from(el.querySelectorAll('.image-carousel__slide'));
      this.btnPrev      = el.querySelector('.image-carousel__arrow--prev');
      this.btnNext      = el.querySelector('.image-carousel__arrow--next');
      this.dotsWrap     = el.querySelector('.image-carousel__dots');

      /* Settings from data-* attributes */
      this.visibleDesktop  = parseInt(el.dataset.visible)          || 5;
      this.visibleMobile   = parseInt(el.dataset.visibleMobile)    || 1;
      this.gap             = parseInt(el.dataset.gap)              || 12;
      this.autoplay        = el.dataset.autoplay === 'true';
      this.autoplaySpeed   = parseInt(el.dataset.autoplaySpeed)    || 3500;
      this.loop            = el.dataset.loop === 'true';
      this.slideAnim       = el.dataset.animation                  || 'slide';
      this.animDuration    = parseInt(el.dataset.animationDuration)|| 450;
      this.animEasing      = el.dataset.animationEasing            || 'ease-out';
      this.entrance        = el.dataset.entrance                   || 'none';
      this.entranceThresh  = (parseInt(el.dataset.entranceThreshold) || 10) / 100;

      this.currentIndex    = 0;
      this._autoplayTimer  = null;
      this._touchStartX    = 0;
      this._touchStartY    = 0;
      this._touching       = false;

      this._onResize   = this._debounce(this._onResize.bind(this), 150);
      this._onKeydown  = this._onKeydown.bind(this);

      this._init();
    }

    _init() {
      /* Apply CSS custom properties for animation */
      this.el.style.setProperty('--ic-anim-duration', this.animDuration + 'ms');
      this.el.style.setProperty('--ic-anim-ease',     this.animEasing);

      this._calcVisible();
      this._applySlideWidths();
      this._setupTransitionMode();
      this._observeImages();
      this._buildDots();
      this._updateControls();
      this._bindEvents();
      this._initEntrance();

      if (this.autoplay && !REDUCED) this._startAutoplay();
    }

    /* ── How many slides are visible at this viewport width ── */
    _calcVisible() {
      this.visible = window.innerWidth >= MOBILE_BP ? this.visibleDesktop : this.visibleMobile;
    }

    /* ── Set slide flex-basis so N fit exactly ── */
    _applySlideWidths() {
      const gapTotal  = this.gap * (this.visible - 1);
      const slideW    = `calc((100% - ${gapTotal}px) / ${this.visible})`;
      this.slides.forEach(s => { s.style.flex = `0 0 ${slideW}`; });
    }

    /* ── Transition mode class on track ── */
    _setupTransitionMode() {
      this.track.classList.remove(
        'image-carousel__track--fade',
        'image-carousel__track--slide-scale'
      );
      if (this.slideAnim === 'fade')        this.track.classList.add('image-carousel__track--fade');
      if (this.slideAnim === 'slide-scale') this.track.classList.add('image-carousel__track--slide-scale');

      if (this.slideAnim !== 'fade') {
        this.track.style.transition = REDUCED
          ? 'none'
          : `transform ${this.animDuration}ms ${this.animEasing}`;
      }
    }

    /* ── Register all images with the lazy observer ── */
    _observeImages() {
      this.el.querySelectorAll('img[data-src]').forEach(img => imageObserver.observe(img));
    }

    /* ── Build dot indicators ── */
    _buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      const count = this._maxIndex() + 1;
      this.dots = Array.from({ length: count }, (_, i) => {
        const btn = document.createElement('button');
        btn.className      = 'image-carousel__dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Slide ${i + 1}`);
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', () => this._goTo(i));
        this.dotsWrap.appendChild(btn);
        return btn;
      });
    }

    /* ── Entrance animation ── */
    _initEntrance() {
      if (this.entrance === 'none' || REDUCED) return;
      this.el.setAttribute('data-entrance', this.entrance);
      this.el.classList.add('ic-entrance-ready');

      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              this.el.classList.add('ic-entrance-done');
              obs.disconnect();
            }
          });
        },
        { threshold: this.entranceThresh }
      );
      obs.observe(this.el);
      this._entranceObs = obs;
    }

    /* ── Max scroll index (last "page") ── */
    _maxIndex() {
      return Math.max(0, this.slides.length - this.visible);
    }

    /* ── Move to index ── */
    _goTo(index) {
      const max = this._maxIndex();
      this.currentIndex = clamp(index, 0, max);

      if (this.slideAnim === 'fade') {
        this.slides.forEach((s, i) => s.classList.toggle('ic-active', i === this.currentIndex));
      } else if (this.slideAnim === 'slide-scale') {
        this.slides.forEach((s, i) => s.classList.toggle('ic-active', i === this.currentIndex));
        this._translateTrack();
      } else {
        this._translateTrack();
      }

      this._updateControls();
    }

    _translateTrack() {
      /* Width of one slide + one gap */
      const slideEl   = this.slides[0];
      const slideW    = slideEl.getBoundingClientRect().width;
      const offset    = this.currentIndex * (slideW + this.gap);
      this.track.style.transform = `translateX(-${offset}px)`;
    }

    /* ── Prev / Next ── */
    _prev() {
      if (this.currentIndex === 0) {
        if (this.loop) this._goTo(this._maxIndex());
      } else {
        this._goTo(this.currentIndex - 1);
      }
    }

    _next() {
      if (this.currentIndex >= this._maxIndex()) {
        if (this.loop) this._goTo(0);
      } else {
        this._goTo(this.currentIndex + 1);
      }
    }

    /* ── Update arrow + dot state ── */
    _updateControls() {
      if (this.btnPrev) this.btnPrev.disabled = !this.loop && this.currentIndex === 0;
      if (this.btnNext) this.btnNext.disabled = !this.loop && this.currentIndex >= this._maxIndex();

      if (this.dots) {
        this.dots.forEach((d, i) => d.setAttribute('aria-selected', i === this.currentIndex ? 'true' : 'false'));
      }
    }

    /* ── Autoplay ── */
    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => this._next(), this.autoplaySpeed);
    }

    _stopAutoplay() {
      clearInterval(this._autoplayTimer);
    }

    /* ── Event binding ── */
    _bindEvents() {
      if (this.btnPrev) this.btnPrev.addEventListener('click', () => { this._prev(); this._resetAutoplay(); });
      if (this.btnNext) this.btnNext.addEventListener('click', () => { this._next(); this._resetAutoplay(); });

      /* Touch / swipe */
      this.trackWrap.addEventListener('touchstart', e => {
        this._touchStartX = e.touches[0].clientX;
        this._touchStartY = e.touches[0].clientY;
        this._touching    = true;
      }, { passive: true });

      this.trackWrap.addEventListener('touchend', e => {
        if (!this._touching) return;
        const dx = e.changedTouches[0].clientX - this._touchStartX;
        const dy = e.changedTouches[0].clientY - this._touchStartY;
        /* Only trigger if horizontal swipe dominates */
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
          dx < 0 ? this._next() : this._prev();
          this._resetAutoplay();
        }
        this._touching = false;
      }, { passive: true });

      /* Pause autoplay on hover / focus */
      this.el.addEventListener('mouseenter', () => this._stopAutoplay());
      this.el.addEventListener('mouseleave', () => { if (this.autoplay && !REDUCED) this._startAutoplay(); });
      this.el.addEventListener('focusin',    () => this._stopAutoplay());
      this.el.addEventListener('focusout',   () => { if (this.autoplay && !REDUCED) this._startAutoplay(); });

      /* Keyboard */
      this.el.addEventListener('keydown', this._onKeydown);

      /* Resize */
      window.addEventListener('resize', this._onResize);
    }

    _onKeydown(e) {
      if (e.key === 'ArrowLeft')  { this._prev(); this._resetAutoplay(); }
      if (e.key === 'ArrowRight') { this._next(); this._resetAutoplay(); }
    }

    _onResize() {
      this._calcVisible();
      this._applySlideWidths();
      this._buildDots();
      /* Clamp index in case visible count increased */
      this.currentIndex = clamp(this.currentIndex, 0, this._maxIndex());
      this._goTo(this.currentIndex);
    }

    _resetAutoplay() {
      if (this.autoplay && !REDUCED) { this._stopAutoplay(); this._startAutoplay(); }
    }

    /* ── Simple debounce ── */
    _debounce(fn, delay) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
    }

    /* ── Teardown ── */
    destroy() {
      this._stopAutoplay();
      window.removeEventListener('resize', this._onResize);
      this.el.removeEventListener('keydown', this._onKeydown);
      if (this._entranceObs) this._entranceObs.disconnect();
      /* Unobserve lazy images */
      this.el.querySelectorAll('img[data-src]').forEach(img => imageObserver.unobserve(img));
      this.el.classList.remove('ic-entrance-ready', 'ic-entrance-done');
      this.track.style.transform  = '';
      this.track.style.transition = '';
    }
  }

  /* =========================================================
     Registry + Shopify theme editor lifecycle
     ========================================================= */
  const registry = new Map();

  function mount(el) {
    if (registry.has(el)) return;
    registry.set(el, new ImageCarousel(el));
  }

  function unmount(el) {
    if (!registry.has(el)) return;
    registry.get(el).destroy();
    registry.delete(el);
  }

  function mountAll() {
    document.querySelectorAll('.image-carousel').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load',    e => { const el = e.target.querySelector('.image-carousel'); if (el) mount(el); });
  document.addEventListener('shopify:section:unload',  e => { const el = e.target.querySelector('.image-carousel'); if (el) unmount(el); });
  document.addEventListener('shopify:section:select',  e => { const el = e.target.querySelector('.image-carousel'); if (el) mount(el); });
  document.addEventListener('shopify:block:select',    e => {
    const el = e.target.closest('.image-carousel');
    if (!el || !registry.has(el)) return;
    const slide = e.target.closest('.image-carousel__slide');
    if (!slide) return;
    const idx = Array.from(el.querySelectorAll('.image-carousel__slide')).indexOf(slide);
    if (idx >= 0) registry.get(el)._goTo(idx);
  });
  document.addEventListener('shopify:section:reorder', e => {
    const el = e.target.querySelector('.image-carousel');
    if (!el) return;
    unmount(el);
    mount(el);
  });
})();
